import { token } from '@atlaskit/tokens';
import {
	HUB_CORNER_RADIUS_CSS_VAR,
	resolveCornerRadiusOverride,
	resolveHubCornerRadius,
} from '@atlassian/company-hub-common/hub-corner-radius';
import { CornerStyle } from '@atlassian/company-hub-common/types';

import { ALIGNMENT, BORDER, CORNERS, type Styles } from '../../types';

const ALIGNMENT_MAP = {
	[ALIGNMENT.LEFT]: 'flex-start',
	[ALIGNMENT.CENTER]: 'center',
	[ALIGNMENT.RIGHT]: 'flex-end',
};

const getBorder = (color: string) => `${token('border.width.selected')} solid ${color}`;

const getBorderStyles = (color: string) => ({
	[BORDER.NONE]: {},
	[BORDER.ALL_SIDES]: { border: getBorder(color) },
	[BORDER.BOTTOM]: {
		borderBottom: getBorder(color),
	},
});

const CORNERS_MAP = {
	[CORNERS.ROUND]: token('radius.small'),
	[CORNERS.SHARP]: 'unset',
};

type LinksMenuStyles = {
	backgroundColor: string;
	border?: string;
	borderBottom?: string;
	borderRadius: string;
	// Local override for the hub corner-radius CSS variable, emitted only on the
	// gated path when the legacy `cornerStyle` maps to an explicit radius (SHARP → 0).
	[HUB_CORNER_RADIUS_CSS_VAR]?: string;
	justifyContent: string;
};

// Map the links-menu-local CORNERS enum onto the shared CornerStyle enum so the
// common resolveCornerRadiusOverride helper can be reused (SHARP → SQUARED,
// ROUND → ROUNDED).
const CORNERS_TO_CORNER_STYLE: Record<CORNERS, CornerStyle> = {
	[CORNERS.ROUND]: CornerStyle.ROUNDED,
	[CORNERS.SHARP]: CornerStyle.SQUARED,
};

/**
 * Resolve the container corner styling.
 *
 * When the hub corner-radius gate is OFF the legacy behaviour is preserved
 * verbatim: `CORNERS.ROUND → radius.small`, `CORNERS.SHARP → unset`.
 *
 * When the gate is ON the container follows the hub-wide `--hub-corner-radius`
 * (symmetric with the spotlight/carousel treatment). An explicit numeric
 * `cornerRadius` (from the config slider) wins and pins a local override
 * (`0` is preserved as truly square); otherwise the legacy `cornerStyle` enum is
 * mapped (`SHARP` → `0`, `ROUND` → inherit the hub value).
 */
const getCornerStyling = (
	cornerStyle: CORNERS,
	cornerRadius: number | undefined,
	isCornerRadiusEnabled: boolean,
): Pick<LinksMenuStyles, 'borderRadius' | typeof HUB_CORNER_RADIUS_CSS_VAR> => {
	if (!isCornerRadiusEnabled) {
		return { borderRadius: CORNERS_MAP[cornerStyle] };
	}

	const override = resolveCornerRadiusOverride(cornerRadius, CORNERS_TO_CORNER_STYLE[cornerStyle]);

	return {
		borderRadius: `var(${HUB_CORNER_RADIUS_CSS_VAR})`,
		...(typeof override === 'number'
			? { [HUB_CORNER_RADIUS_CSS_VAR]: `${resolveHubCornerRadius(override)}px` }
			: {}),
	};
};

export const getLinksMenuStyles = (
	styles: Styles,
	isCornerRadiusEnabled = false,
): LinksMenuStyles => {
	const { alignment, fillColor, borderColor, border, cornerStyle, cornerRadius } = styles;
	return {
		justifyContent: ALIGNMENT_MAP[alignment],
		backgroundColor: fillColor,
		...getBorderStyles(borderColor)[border],
		...getCornerStyling(cornerStyle, cornerRadius, isCornerRadiusEnabled),
	};
};
